import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ScheduleIcon from '@mui/icons-material/Schedule';
import VideocamIcon from '@mui/icons-material/Videocam';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import Footer from '../../components/Footer';
import CompanyStrip from '../../components/home/CompanyStrip';
import {
  MarketingPage, Hero, Ticker, Statement, BeforeAfter, SectionHead, FeatureRow,
  Compare, Journey, Faq, FinalCta, Cta
} from '../../components/marketing/Blocks';
import { TeamHeroVisual, ExpertHeroVisual, FeatureVisual } from '../../components/marketing/Visuals';
import { audiences, getAudience } from '../../data/audiences';
import { cohorts, formatDate } from '../../data/liveClasses';
import { SKILL_DEVELOPMENT } from '../../data/programs';
import { formatInr, SESSION_PRICE_INR, SESSION_LIST_PRICE_INR } from '../../data/roadmapSession';

// Next upcoming cohort for each course title
const nextCohortFor = (title) =>
  cohorts
    .filter((c) => c.course.title === title)
    .sort((a, b) => a.startDate.localeCompare(b.startDate))[0];

const setMetaDescription = (content) => {
  let tag = document.querySelector('meta[name="description"]');
  const previous = tag?.getAttribute('content') ?? null;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', 'description');
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
  return previous;
};

const offers = {
  team: (offer) => ({
    tag: '3-month programme',
    price: SKILL_DEVELOPMENT.priceInr,
    listPrice: SKILL_DEVELOPMENT.listPriceInr,
    includes: [
      'A real engineering team with AI engineers',
      'Senior engineer review on every pull request',
      `${offer.letter} and pay slips`,
      'Interview guidance',
      'Support after you get the job'
    ],
    cta: { label: 'Join the team', to: `/billing/${SKILL_DEVELOPMENT.slug}`, primary: true },
    note: 'Limited seats in every team',
    link: { label: 'Not sure yet? Talk to a mentor first', to: '/#book-session' }
  }),
  expert: () => ({
    tag: '1:1 call · 30 minutes',
    price: SESSION_PRICE_INR,
    listPrice: SESSION_LIST_PRICE_INR,
    includes: [
      'With a senior engineer',
      'An honest check of where you stand',
      'Line-by-line CV review',
      'A step-by-step plan for your switch'
    ],
    cta: { label: 'Book my call', to: '/#book-session', primary: true },
    note: 'Evening and weekend slots available'
  })
};

const Offer = ({ offer }) => {
  const o = offers[offer.type](offer);
  return (
    <section className="mkt-section" id="offer">
      <div className="container">
        <div className="mkt-offer" data-reveal>
          <div className="mkt-offer-copy">
            <span className="mkt-eyebrow">{offer.type === 'team' ? 'Join the real engineering team' : 'Talk to an expert'}</span>
            <h2 className="mkt-h2">{offer.title}</h2>
            <p>{offer.text}</p>
          </div>
          <div className="mkt-offer-card">
            <span className="mkt-offer-tag">{o.tag}</span>
            <div className="mkt-price">
              <strong>{formatInr(o.price)}</strong>
              {o.listPrice > o.price && <s>{formatInr(o.listPrice)}</s>}
            </div>
            {o.listPrice > o.price && <span className="mkt-save">You save {formatInr(o.listPrice - o.price)}</span>}
            <ul>
              {o.includes.map((item) => (
                <li key={item}><CheckCircleRoundedIcon sx={{ fontSize: 19 }} /> {item}</li>
              ))}
            </ul>
            <Cta {...o.cta} />
            <span className="mkt-offer-note"><LocalFireDepartmentIcon sx={{ fontSize: 17 }} /> {o.note}</span>
            {o.link && <Link to={o.link.to} className="mkt-offer-link">{o.link.label}</Link>}
          </div>
        </div>
      </div>
    </section>
  );
};

const LiveClasses = ({ live }) => {
  const list = live.courses.map(nextCohortFor).filter(Boolean);
  return (
    <section className="mkt-section" id="live-classes">
      <div className="container">
        <SectionHead kicker="Live classes" title={live.title} text={live.text} />
        <div className="mkt-posters">
          {list.map((cohort, i) => {
            const { course } = cohort;
            const Icon = course.icon;
            return (
              <article
                className="mkt-poster"
                key={cohort.id}
                data-reveal
                style={{ '--i': i, '--c1': course.accent[0], '--c2': course.accent[1] }}
              >
                <div className="mkt-poster-top">
                  <span className="mkt-poster-icon"><Icon /></span>
                  <span className="mkt-badge">{cohort.batch} batch</span>
                </div>
                <h3>{course.title}</h3>
                <p>{course.description}</p>
                <div className="mkt-poster-meta">
                  <span><CalendarMonthIcon sx={{ fontSize: 16 }} /> Starts {formatDate(cohort.startDate)}</span>
                  <span><ScheduleIcon sx={{ fontSize: 16 }} /> {cohort.schedule}</span>
                  <span><VideocamIcon sx={{ fontSize: 16 }} /> Live · {cohort.duration}</span>
                </div>
                <Cta label="Request a seat" to={`/live-classes?course=${encodeURIComponent(course.title)}#request-form`} />
              </article>
            );
          })}
        </div>
        <div className="mkt-more"><Cta label="See all live cohorts" to="/live-classes" /></div>
      </div>
    </section>
  );
};

const Prep = ({ prep }) => (
  <section className="mkt-section" id="interview-prep">
    <div className="container">
      <SectionHead kicker="Interview prep · free" title={prep.title} text={prep.text} />
      <div className="mkt-posters">
        {prep.tracks.map((track, i) => (
          <Link to={`/interview-prep/${track.slug}`} className="mkt-poster mkt-poster-link" key={track.slug} data-reveal style={{ '--i': i % 3 }}>
            <div className="mkt-poster-top">
              <span className="mkt-poster-icon"><QuestionAnswerOutlinedIcon /></span>
              <span className="mkt-badge is-free">Free</span>
            </div>
            <h3>{track.name}</h3>
            <p>{track.text}</p>
            <span className="mkt-poster-go">Practise questions <ArrowForwardIcon sx={{ fontSize: 16 }} /></span>
          </Link>
        ))}
      </div>
      <div className="mkt-more"><Cta label="Browse all interview questions" to="/interview-prep" /></div>
    </div>
  </section>
);

const heroVisuals = {
  team: (v) => <TeamHeroVisual letter={v.letter} />,
  expert: () => <ExpertHeroVisual />
};

// One marketing page per audience at /<slug>; copy lives in data/audiences.js
const Audience = ({ slug }) => {
  const page = getAudience(slug);

  useEffect(() => {
    document.title = page.seo.title;
    const previous = setMetaDescription(page.seo.description);
    return () => {
      document.title = 'Codeazy';
      if (previous !== null) setMetaDescription(previous);
    };
  }, [page]);

  const { hero, ticker, statement, beforeAfter, featuresHead, features, offer, live, prep, compare, journey, faqs, finalCta } = page;
  const others = audiences.filter((a) => a.slug !== page.slug);
  const sticky = offer && { text: offer.sticky, cta: { label: offer.type === 'team' ? 'Join now' : 'Book a call', to: '#offer' } };

  return (
    <MarketingPage accent={page.accent} sticky={sticky}>
      <Hero {...hero} visual={heroVisuals[hero.visual.type](hero.visual)} />
      <Ticker items={ticker} />
      <div className="mkt-companies"><CompanyStrip /></div>
      <Statement {...statement} />
      <BeforeAfter {...beforeAfter} />

      {features && (
        <section className="mkt-section">
          <div className="container">
            <SectionHead {...featuresHead} center />
            <div className="mkt-features">
              {features.map((f, i) => (
                <FeatureRow
                  key={f.title}
                  {...f}
                  index={i}
                  reverse={i % 2 === 1}
                  visual={<FeatureVisual type={f.visual} letter={hero.visual.letter} />}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {offer && <Offer offer={offer} />}
      {live && <LiveClasses live={live} />}
      {prep && <Prep prep={prep} />}
      {compare && <Compare {...compare} />}
      <Journey {...journey} />
      <Faq items={faqs} />

      <FinalCta {...finalCta}>
        <nav className="mkt-others" aria-label="Other programmes">
          <span>Not you?</span>
          {others.map((a) => (
            <Link to={`/${a.slug}`} key={a.slug}>{a.navLabel} <ArrowForwardIcon sx={{ fontSize: 15 }} /></Link>
          ))}
          <Link to="/workshops">Free workshops <ArrowForwardIcon sx={{ fontSize: 15 }} /></Link>
        </nav>
      </FinalCta>

      <Footer />
    </MarketingPage>
  );
};

export default Audience;
